/**
 * EDR Connector API client — backend at https://edr-demo.smplify.com.
 *
 * Auth: reuses the existing admin bearer token from `apiClient` (the
 * EDR backend validates RS256 admin JWTs issued by the Smplify MCP
 * OAuth bridge). If/when the EDR backend rejects the existing token,
 * we'll need to add a dedicated token storage — for now we keep one
 * source of truth so users don't have to log in twice.
 *
 * Most EDR responses are unwrapped DTOs (e.g. `AlertList`, `RuleList`),
 * not the `{success,data,…}` envelope used by `apiClient`.
 */
import { getMcpAccessToken, getAuthToken, getTenantId } from "./apiClient";

const BASE_URL =
  (import.meta.env.VITE_EDR_BACKEND_URL as string | undefined) ||
  "https://edr-demo.smplify.com";

export class EdrApiError extends Error {
  status: number;
  code: string;
  constructor(message: string, status: number, code = "UNKNOWN") {
    super(message);
    this.name = "EdrApiError";
    this.status = status;
    this.code = code;
  }
}

interface EdrRequestOptions {
  params?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Extra request headers (e.g. X-Smplify-Confirm-Action for RTR). */
  headers?: Record<string, string>;
}

export async function edrGet<T>(path: string, options: EdrRequestOptions = {}): Promise<T> {
  return edrRequest<T>("GET", path, undefined, options);
}

export async function edrPatch<T>(
  path: string,
  body?: unknown,
  options: EdrRequestOptions = {},
): Promise<T> {
  return edrRequest<T>("PATCH", path, body, options);
}

export async function edrPost<T>(
  path: string,
  body?: unknown,
  options: EdrRequestOptions = {},
): Promise<T> {
  return edrRequest<T>("POST", path, body, options);
}

export async function edrPut<T>(
  path: string,
  body?: unknown,
  options: EdrRequestOptions = {},
): Promise<T> {
  return edrRequest<T>("PUT", path, body, options);
}

export async function edrDelete<T = void>(
  path: string,
  body?: unknown,
  options: EdrRequestOptions = {},
): Promise<T> {
  return edrRequest<T>("DELETE", path, body, options);
}

/** True when the route hasn't shipped yet (404 / 405). Lets UI render a
 * "Coming online" empty state instead of an error. */
export function isMissingRoute(err: unknown): boolean {
  return err instanceof EdrApiError && (err.status === 404 || err.status === 405);
}

async function edrRequest<T>(
  method: "GET" | "PATCH" | "POST" | "PUT" | "DELETE",
  path: string,
  body: unknown,
  options: EdrRequestOptions = {},
): Promise<T> {
  let url = `${BASE_URL}${path}`;
  if (options.params) {
    const sp = new URLSearchParams();
    Object.entries(options.params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") sp.append(k, String(v));
    });
    const qs = sp.toString();
    if (qs) url += `?${qs}`;
  }

  const headers: Record<string, string> = { Accept: "application/json" };
  // Send both unconditionally per integration guide §3.3 — EDR reads tenant
  // from JWT but Lighthouse-style backends behind the same gateway will need
  // the header, and it's a no-op when ignored.
  const tenantId = getTenantId();
  if (tenantId) headers["X-Tenant-Id"] = tenantId;
  // Prefer MCP token (issued for EDR/Lighthouse); fall back to admin token.
  const token = getMcpAccessToken() || getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  // Caller-supplied headers override defaults (e.g. confirm-action).
  if (options.headers) Object.assign(headers, options.headers);

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      signal: options.signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new Error(
      "Unable to reach the EDR backend. This may be a network issue or a CORS restriction. Check your connection or contact your administrator.",
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // non-JSON body — fall through with raw text in the error
  }

  if (!response.ok) {
    const errObj = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
    throw new EdrApiError(
      errObj?.message || (typeof parsed === "string" ? parsed : `Request failed (${response.status})`),
      response.status,
      errObj?.code || "HTTP_ERROR",
    );
  }

  return parsed as T;
}
